import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState, type FormEvent } from "react";
import {
  DEFAULT_LABEL_LAYOUT,
  fitLabelSize,
  LABEL_PRESETS,
  labelLayoutSchema,
  labelsPerPage,
  PAPER_SIZES,
  type LabelLayout,
  type LabelLayoutsDto,
} from "@klantpunt/shared";
import { Modal } from "../../app/Modal";
import { api } from "../../lib/api";
import { can, useMe } from "../../lib/auth";

const KEY = ["label-layouts"];
const CUSTOM = "custom";
/** Labelprinter: één label per pagina en de pagina is precies het label. */
const ROLL = "roll";

/** Getal in invoer: komma of punt, maximaal één decimaal. */
const num = (s: string) => {
  const n = Number(s.trim().replace(",", "."));
  return s.trim() && Number.isFinite(n) ? n : NaN;
};
const str = (n: number) => String(Math.round(n * 10) / 10).replace(".", ",");

type Form = Record<"pageW" | "pageH" | "cols" | "rows" | "labelW" | "labelH" | "gapX" | "gapY", string> & {
  name: string;
  cutLines: boolean;
};
const toForm = (l: LabelLayout): Form => ({
  name: l.name,
  cutLines: l.cutLines,
  pageW: str(l.pageW),
  pageH: str(l.pageH),
  cols: String(l.cols),
  rows: String(l.rows),
  labelW: str(l.labelW),
  labelH: str(l.labelH),
  gapX: str(l.gapX),
  gapY: str(l.gapY),
});
const fromForm = (f: Form) =>
  labelLayoutSchema.safeParse({
    name: f.name,
    cutLines: f.cutLines,
    pageW: num(f.pageW),
    pageH: num(f.pageH),
    cols: num(f.cols),
    rows: num(f.rows),
    labelW: num(f.labelW),
    labelH: num(f.labelH),
    gapX: num(f.gapX || "0"),
    gapY: num(f.gapY || "0"),
  });

/** Welk papierformaat (staand of liggend) hoort bij deze paginamaat? */
function paperOf(w: number, h: number, l?: { cols: number; rows: number; labelW: number; labelH: number }) {
  const near = (a: number, b: number) => Math.abs(a - b) < 0.6;
  if (l && l.cols === 1 && l.rows === 1 && near(w, l.labelW) && near(h, l.labelH))
    return { key: ROLL, landscape: false };
  for (const p of PAPER_SIZES) {
    if (near(w, p.w) && near(h, p.h)) return { key: p.key as string, landscape: false };
    if (near(w, p.h) && near(h, p.w)) return { key: p.key as string, landscape: true };
  }
  return { key: CUSTOM, landscape: false };
}

/**
 * Formaat kiezen voor de QR-labels: voorinstelling (stickervel of labelprinter), of eigen papiermaat, aantal labels
 * per rij en rijen per pagina. Het gekozen formaat komt bovenaan de laatst gebruikte formaten van de organisatie.
 */
export function LabelPrintDialog({
  count,
  onClose,
  onPrint,
}: {
  /** Aantal labels dat gemaakt wordt. */
  count: number;
  onClose: () => void;
  /** Maakt en opent de PDF; gooit bij een fout (de dialoog blijft dan open). */
  onPrint: (layout: LabelLayout, skip: number) => Promise<void>;
}) {
  const qc = useQueryClient();
  const { data: me } = useMe();
  const canSave = can(me, "asset.manage");
  const q = useQuery({ queryKey: KEY, queryFn: () => api<LabelLayoutsDto>("/org/label-layouts") });
  const recent = q.data?.recent ?? [];
  const [form, setForm] = useState<Form | null>(null);
  const [choice, setChoice] = useState<string>("");
  const [start, setStart] = useState("1");
  const [busy, setBusy] = useState(false);
  const [customPage, setCustomPage] = useState(false);

  // Eerste keer: het laatst gebruikte formaat van de organisatie, anders het standaardvel.
  const f = form ?? toForm(recent[0] ?? DEFAULT_LABEL_LAYOUT);
  const sel = choice || (recent[0] ? "recent-0" : "preset-0");
  const parsed = fromForm(f);
  const layout = parsed.success ? parsed.data : null;
  const per = layout ? labelsPerPage(layout) : 0;
  const skipN = num(start) - 1;
  const skipOk = Number.isInteger(skipN) && skipN >= 0 && (!per || skipN < per);
  const detected = paperOf(num(f.pageW), num(f.pageH), {
    cols: num(f.cols),
    rows: num(f.rows),
    labelW: num(f.labelW),
    labelH: num(f.labelH),
  });
  const paper = customPage ? { key: CUSTOM, landscape: false } : detected;
  const roll = paper.key === ROLL;

  const edit = (patch: Partial<Form>, keepRoll = roll) => {
    const next = { ...f, name: "Eigen formaat", ...patch };
    // Labelprinter: de pagina volgt de labelmaat. Eén label per pagina (1 × 1) schakelt daar ook naar over.
    const toRoll = keepRoll || (("cols" in patch || "rows" in patch) && num(next.cols) === 1 && num(next.rows) === 1);
    if (toRoll) Object.assign(next, { cols: "1", rows: "1", pageW: next.labelW, pageH: next.labelH });
    setForm(next);
    setChoice(CUSTOM);
  };
  const set = (k: keyof Form) => (e: { target: { value: string } }) => edit({ [k]: e.target.value });

  function pick(v: string) {
    setChoice(v);
    setCustomPage(false);
    const [kind, i] = v.split("-");
    const l = kind === "recent" ? recent[Number(i)] : kind === "preset" ? LABEL_PRESETS[Number(i)] : undefined;
    if (l) setForm(toForm(l));
  }
  function pickPaper(key: string, landscape = paper.landscape) {
    setCustomPage(false);
    if (key === ROLL) {
      setForm({ ...f, name: "Eigen formaat", cols: "1", rows: "1", pageW: f.labelW, pageH: f.labelH, cutLines: false });
      setChoice(CUSTOM);
      return;
    }
    const p = PAPER_SIZES.find((x) => x.key === key);
    if (!p) {
      // Eigen paginamaat, los van de labelmaat (bijv. een vel met meerdere labels).
      setCustomPage(true);
      return edit({}, false);
    }
    const [w, h] = landscape ? [p.h, p.w] : [p.w, p.h];
    edit({ pageW: str(w), pageH: str(h) }, false);
  }
  function fitLabels() {
    const g = {
      pageW: num(f.pageW),
      pageH: num(f.pageH),
      cols: num(f.cols),
      rows: num(f.rows),
      gapX: num(f.gapX || "0"),
      gapY: num(f.gapY || "0"),
    };
    if (Object.values(g).some((n) => !Number.isFinite(n) || n < 0) || g.cols < 1 || g.rows < 1) return;
    // Eén label per pagina (labelprinter): tot de rand; op een vel 5 mm rand voor de printer.
    const s = fitLabelSize(g, g.cols * g.rows > 1 ? 5 : 0);
    edit({ labelW: str(s.labelW), labelH: str(s.labelH) });
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!layout || !skipOk) return;
    setBusy(true);
    try {
      await onPrint(layout, skipN);
      if (canSave)
        void api<LabelLayoutsDto>("/org/label-layouts/recent", { method: "POST", body: layout })
          .then((r) => qc.setQueryData(KEY, r))
          .catch(() => undefined);
      onClose();
    } catch {
      // De aanroeper meldt de fout; dialoog blijft open.
    } finally {
      setBusy(false);
    }
  }

  const pages = per && skipOk ? Math.ceil((count + skipN) / per) : 0;
  const err = !parsed.success ? (parsed.error.issues[0]?.message ?? "Controleer de maten") : null;

  return (
    <Modal
      title="QR-labels printen"
      onClose={onClose}
      busy={busy}
      wide
      footer={
        <>
          <button type="button" className="btn ghost" onClick={onClose} disabled={busy}>
            Annuleren
          </button>
          <button type="submit" form="labelform" className="btn primary" disabled={busy || !layout || !skipOk}>
            {busy ? "Bezig…" : `PDF maken (${count} label${count === 1 ? "" : "s"})`}
          </button>
        </>
      }
    >
      <form id="labelform" className="stack" onSubmit={submit}>
        <label className="field">
          <span>Formaat</span>
          <select value={sel} onChange={(e) => pick(e.target.value)}>
            {recent.length > 0 && (
              <optgroup label="Laatst gebruikt in je organisatie">
                {recent.map((l, i) => (
                  <option key={`r${i}`} value={`recent-${i}`}>
                    {describe(l)}
                  </option>
                ))}
              </optgroup>
            )}
            <optgroup label="Stickervellen en labelprinters">
              {LABEL_PRESETS.map((l, i) => (
                <option key={`p${i}`} value={`preset-${i}`}>
                  {l.name}
                </option>
              ))}
            </optgroup>
            <option value={CUSTOM}>Eigen formaat</option>
          </select>
        </label>

        <div className="labelprint">
          <div className="stack">
            <div className="labelnums">
              <label className="field">
                <span>Papier</span>
                <select value={paper.key} onChange={(e) => pickPaper(e.target.value)}>
                  <option value={ROLL}>Labelprinter (pagina = label)</option>
                  {PAPER_SIZES.map((p) => (
                    <option key={p.key} value={p.key}>
                      {p.label}
                    </option>
                  ))}
                  <option value={CUSTOM}>Eigen paginamaat</option>
                </select>
              </label>
              {roll ? (
                <p className="hint">Elk label wordt een eigen pagina van precies deze maat.</p>
              ) : paper.key === CUSTOM ? (
                <div className="labelnums">
                  <label className="field">
                    <span>Breedte (mm)</span>
                    <input inputMode="decimal" value={f.pageW} onChange={set("pageW")} />
                  </label>
                  <label className="field">
                    <span>Hoogte (mm)</span>
                    <input inputMode="decimal" value={f.pageH} onChange={set("pageH")} />
                  </label>
                </div>
              ) : (
                <label className="field inline">
                  <input
                    type="checkbox"
                    checked={paper.landscape}
                    onChange={(e) => pickPaper(paper.key, e.target.checked)}
                  />
                  <span>Liggend</span>
                </label>
              )}
              {!roll && (
                <>
                  <label className="field">
                    <span>Labels per rij</span>
                    <input inputMode="numeric" value={f.cols} onChange={set("cols")} />
                  </label>
                  <label className="field">
                    <span>Rijen per pagina</span>
                    <input inputMode="numeric" value={f.rows} onChange={set("rows")} />
                  </label>
                </>
              )}
              <label className="field">
                <span>Labelbreedte (mm)</span>
                <input inputMode="decimal" value={f.labelW} onChange={set("labelW")} />
              </label>
              <label className="field">
                <span>Labelhoogte (mm)</span>
                <input inputMode="decimal" value={f.labelH} onChange={set("labelH")} />
              </label>
              {!roll && (
                <label className="field">
                  <span>Beginnen bij label</span>
                  <input
                    inputMode="numeric"
                    value={start}
                    aria-invalid={!skipOk}
                    onChange={(e) => setStart(e.target.value)}
                  />
                  <span className="hint">Voor een deels gebruikt vel</span>
                </label>
              )}
            </div>
            <details className="labelmore">
              <summary>Tussenruimte en snijlijnen</summary>
              <div className="labelnums">
                <label className="field">
                  <span>Tussenruimte breed (mm)</span>
                  <input inputMode="decimal" value={f.gapX} onChange={set("gapX")} />
                </label>
                <label className="field">
                  <span>Tussenruimte hoog (mm)</span>
                  <input inputMode="decimal" value={f.gapY} onChange={set("gapY")} />
                </label>
                <label className="field inline">
                  <input type="checkbox" checked={f.cutLines} onChange={(e) => edit({ cutLines: e.target.checked })} />
                  <span>Snijlijnen tekenen</span>
                </label>
                <button type="button" className="btn small" onClick={fitLabels}>
                  Labels passend maken
                </button>
              </div>
            </details>
          </div>
          {layout && <SheetPreview layout={layout} skip={skipOk ? skipN : 0} count={count} />}
        </div>
        <p className={err ? "small warn" : "small"} role={err ? "alert" : undefined}>
          {err ??
            (roll
              ? `${pages} pagina${pages === 1 ? "" : "'s"} van ${f.labelW} × ${f.labelH} mm. Kies in de printer dezelfde labelmaat en print op ware grootte (100 %).`
              : `${per} label${per === 1 ? "" : "s"} per pagina · ${pages} pagina${pages === 1 ? "" : "'s"}. Print op ware grootte (100 %), niet passend gemaakt. Kies op de iPad of printer hetzelfde papierformaat.`)}
        </p>
      </form>
    </Modal>
  );
}

function describe(l: LabelLayout) {
  if (l.name !== "Eigen formaat") return l.name;
  const paper = paperOf(l.pageW, l.pageH, l);
  if (paper.key === ROLL) return `Labelprinter ${str(l.labelW)} × ${str(l.labelH)} mm`;
  const p =
    paper.key === CUSTOM ? `${str(l.pageW)} × ${str(l.pageH)} mm` : `${paper.key}${paper.landscape ? " liggend" : ""}`;
  return `${p}, ${l.cols} × ${l.rows} (${str(l.labelW)} × ${str(l.labelH)} mm)`;
}

/** Schets van de eerste pagina: welke labels bedrukt worden en welke worden overgeslagen. */
function SheetPreview({ layout: l, skip, count }: { layout: LabelLayout; skip: number; count: number }) {
  const per = labelsPerPage(l);
  const gridW = l.cols * l.labelW + (l.cols - 1) * l.gapX;
  const gridH = l.rows * l.labelH + (l.rows - 1) * l.gapY;
  const ox = (l.pageW - gridW) / 2;
  const oy = (l.pageH - gridH) / 2;
  return (
    <svg
      className="labelpreview"
      viewBox={`0 0 ${l.pageW} ${l.pageH}`}
      role="img"
      aria-label={`Voorbeeld van de eerste pagina: ${Math.min(count, per - skip)} van ${per} labels bedrukt`}
    >
      <rect width={l.pageW} height={l.pageH} className="page" />
      {Array.from({ length: per }, (_, n) => {
        const used = n >= skip && n < skip + count;
        return (
          <rect
            key={n}
            x={ox + (n % l.cols) * (l.labelW + l.gapX)}
            y={oy + Math.floor(n / l.cols) * (l.labelH + l.gapY)}
            width={l.labelW}
            height={l.labelH}
            rx={Math.min(l.labelW, l.labelH) * 0.06}
            className={used ? "used" : "free"}
          />
        );
      })}
    </svg>
  );
}
